import type {
  CustomerProfileResponse,
  ProfileSSEEvent,
  ProfileProgressEvent,
} from '../model/CustomerProfileTypes';
import { SSE_ERROR_MESSAGES } from '@/common/util/sseUtil';

/**
 * SSE 이벤트 핸들러 인터페이스
 * CustomerApiAgent와 prefetchCustomerProfileStream에서 공통으로 사용
 */
export interface ProfileSSEEventHandlers {
  onProgress: (event: ProfileProgressEvent) => void;
  onToken: (token: string) => void;
  onComplete: (data: CustomerProfileResponse) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

/**
 * token 이벤트의 content에서 문자열 추출
 * content가 string이면 그대로, object면 text/message 필드 추출
 */
function extractTokenContent(rawContent: unknown): string | null {
  if (typeof rawContent === 'string') {
    return rawContent;
  }

  if (typeof rawContent === 'object' && rawContent !== null) {
    const contentObj = rawContent as Record<string, unknown>;
    const text = (contentObj.text || contentObj.message || '') as string;
    return text || null;
  }

  return null;
}

/**
 * SSE 이벤트 파싱 및 핸들러 호출
 * @param eventData - SSE 이벤트 데이터 (JSON 문자열)
 * @param handlers - 이벤트 타입별 핸들러
 * @returns 파싱 성공 여부
 */
export function parseProfileSSEEvent(
  eventData: string,
  handlers: ProfileSSEEventHandlers
): boolean {
  try {
    const parsed: ProfileSSEEvent = JSON.parse(eventData);

    switch (parsed.event) {
      case 'progress':
        handlers.onProgress(parsed);
        break;

      case 'token': {
        const tokenContent = extractTokenContent(parsed.content);
        if (tokenContent) {
          handlers.onToken(tokenContent);
        }
        break;
      }

      case 'complete':
        handlers.onComplete(parsed.data);
        break;

      case 'done':
        handlers.onDone();
        break;

      case 'error': {
        const errorMsg =
          typeof parsed.message === 'string'
            ? parsed.message
            : SSE_ERROR_MESSAGES.UNKNOWN_ERROR;
        handlers.onError(errorMsg);
        break;
      }
    }

    return true;
  } catch {
    return false;
  }
}
